import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ProfileType } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";

import LocationAutocomplete, {
  LocationAutocompleteHandle,
} from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledAutocomplete from "components/Styled/Autocomplete";
import StyledTextField from "components/Styled/TextField";
import { fetcherAuth } from "utils/api";
import { hasSocialMediaProfile } from "utils/influencer";
import { makeDeepCopy } from "utils/updateLocalState";

import AboutFormLinks from "../../AboutForm/Links";
import {
  SocialMediaFields,
  SocialMediaLinks,
  isValidUsername,
  sanitizeInput,
} from "../../AboutForm/helpers";
import StyledProPage from "../StyledPage";

interface ProOnboardingAboutFormProps {
  handleNext: (onboardingForm?: Map) => void;
  handleBack?: () => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  onFinishSetUpLater?: () => void;
}

const ProOnboardingAboutForm = ({
  handleNext,
  handleBack,
  onboardingForm,
  setOnboardingForm,
  onFinishSetUpLater,
}: ProOnboardingAboutFormProps) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(-1);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const locationAutocompleteRef = useRef<LocationAutocompleteHandle>(null);

  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    setOnboardingForm((prev) => ({
      ...prev,
      city: userLocation.address,
      location: userLocation.country_code,
      addressComponents: userLocation.address_components,
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    }));
  };

  const handleSubmit = async () => {
    if (Object.values(linksValidation).some((v) => v !== undefined)) {
      return;
    }

    for (const key in onboardingForm) {
      if (key in SocialMediaFields && onboardingForm[key]) {
        const input = sanitizeInput(onboardingForm[key]);
        if (input && !isValidUsername(input) && key !== "portfolioUrl") {
          return;
        }
      }
    }

    if (!onboardingForm?.name) {
      setError("Please fill in your name to continue");
      return;
    }

    let formToSubmit: Map = onboardingForm;
    // Always re-resolve against the input text — state may be stale if the
    // user edited the prefilled text without fully clearing it.
    const result = await locationAutocompleteRef.current?.resolveLocation();
    if (result?.resolved?.country_code) {
      formToSubmit = {
        ...formToSubmit,
        city: result.resolved.address,
        location: result.resolved.country_code,
        addressComponents: result.resolved.address_components,
        latitude: result.resolved.latitude,
        longitude: result.resolved.longitude,
      };
      setOnboardingForm(formToSubmit);
    } else if (result?.hasText && !result?.valid) {
      setActiveQuestionIndex(1);
      setError("Please select a location from the dropdown suggestions");
      return;
    }

    if (
      !formToSubmit?.addressComponents ||
      formToSubmit?.addressComponents.length < 2
    ) {
      setActiveQuestionIndex(1);
      setError("Please fill in a city or zip code");
      return;
    }
    if (!formToSubmit?.location) {
      setActiveQuestionIndex(1);
      setError(
        "We could not extract the country from your location, please input the full address like `Kosovo Polje, Bosnia and Herzegovina`",
      );
      return;
    }

    if (
      formToSubmit?.latitude &&
      formToSubmit?.longitude &&
      formToSubmit?.latitude === formToSubmit?.longitude
    ) {
      setError(
        "Cannot store this location, please retype the address and try again",
      );
      setOnboardingForm((prev) => ({
        ...prev,
        latitude: undefined,
        longitude: undefined,
      }));
      return;
    }

    if (!hasSocialMediaProfile(formToSubmit)) {
      setError("Please provide at least one social media profile to continue");
      return;
    }

    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...formToSubmit,
        },
      );
      handleNext(formToSubmit);
      setLoading(false);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const setActiveQuestionAutomatically = () => {
    if (!onboardingForm?.name) {
      setActiveQuestionIndex(0);
      return;
    }
    const locationCompleted =
      !!onboardingForm?.addressComponents &&
      onboardingForm?.addressComponents.length >= 2 &&
      !!onboardingForm?.location;
    if (!locationCompleted) {
      setActiveQuestionIndex(1);
      return;
    }
    if (
      !onboardingForm?.profileTypes ||
      onboardingForm?.profileTypes.length === 0
    ) {
      setActiveQuestionIndex(2);
      return;
    }

    if (!hasSocialMediaProfile(onboardingForm)) {
      setActiveQuestionIndex(3);
      return;
    }
    setActiveQuestionIndex(-1);
  };

  useEffect(() => {
    setActiveQuestionAutomatically();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(onboardingForm?.name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(onboardingForm?.addressComponents),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(
      onboardingForm?.profileTypes && onboardingForm?.profileTypes.length > 0,
    ),
  ]);

  return (
    <StyledProPage
      title={"Let's Get To Know You ✍️"}
      onBack={handleBack}
      onNext={handleSubmit}
      onFinishSetUpLater={onFinishSetUpLater}
      stepNumber={1}
      description={
        "This will help us personalize your experience and connect you with the right opportunities."
      }
      buttonNext={"Continue to Templates"}
      buttonBack={"Back"}
      loading={loading}
      body={
        <Grid container gap={2} flexDirection={"column"}>
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              name="name"
              placeholder="First Last"
              onChange={handleChange}
              required
              value={onboardingForm?.name || ""}
              completed={!!onboardingForm?.name}
              label="Your Name"
              allowCollapsible
              isActive={activeQuestionIndex === 0}
              error={error}
            />
          </Grid>
          <Grid item xs={12}>
            <LocationAutocomplete
              ref={locationAutocompleteRef}
              handleLocationChange={handleLocationChange}
              userLocation={{
                address: onboardingForm.city,
                longitude: onboardingForm.longitude,
                latitude: onboardingForm.latitude,
                country_code: onboardingForm.location,
                address_components: onboardingForm.addressComponents,
              }}
              customStyling={{
                height: 52,
                width: "100%",
                borderRadius: 4,
                border: `1px solid ${grey[300]}`,
                px: 1,

                "&:hover, &:focus": {
                  outline: "none",
                },
              }}
              label="City or Zip Code"
              required
              allowCollapsible
              completed={
                !!onboardingForm?.addressComponents &&
                onboardingForm?.addressComponents.length >= 2 &&
                !!onboardingForm?.location
              }
              isActive={activeQuestionIndex === 1}
            />

            {error && activeQuestionIndex === 1 && (
              <Typography sx={{ color: "error.main" }}>{error}</Typography>
            )}
          </Grid>

          <Box>
            <StyledAutocomplete
              required
              label="Who are you?"
              multiple
              options={Object.values(ProfileType)}
              value={onboardingForm?.profileTypes || []}
              onChange={(_: any, newValue: ProfileType[]) => {
                setOnboardingForm((p) => {
                  if (!p) return p;
                  const copy = { ...p };
                  copy.profileTypes = newValue;
                  return copy;
                });
              }}
              completed={
                onboardingForm?.profileTypes &&
                onboardingForm?.profileTypes?.length > 0
              }
              isActive={activeQuestionIndex === 2}
              error={activeQuestionIndex === 2 && error ? error : ""}
            />
            {onboardingForm?.profileTypes?.includes(ProfileType.other) && (
              <TextField
                placeholder="Please specify"
                value={onboardingForm?.otherType || ""}
                onChange={(e: CustomEvent) => {
                  const { value } = e.target;

                  setOnboardingForm((p) => {
                    if (!p) return p;
                    const copy = { ...p };
                    copy.otherType = value;
                    return copy;
                  });
                }}
                variant="standard"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <AboutFormLinks
            onboardingForm={onboardingForm}
            setOnboardingForm={setOnboardingForm}
            setLinksValidation={setLinksValidation}
            links={links}
            setLinks={setLinks}
            validation={linksValidation}
            activeQuestionIndex={activeQuestionIndex}
            error={error}
          />
        </Grid>
      }
    />
  );
};

export default ProOnboardingAboutForm;
